import { inject, Injectable } from "@angular/core";
import { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { filter, from, map, mergeMap, Observable, of, switchMap, tap, toArray } from "rxjs";
import { ProjectImageMetadata } from "./project-image-metadata";

@Injectable({
  providedIn: "root",
})
export class ProjectImageMigrationService {
  private projectImageService = inject(ProjectImageService);

  migrateOldProjectImages(project: ScopedProject): Observable<boolean> {
    const images: ProjectImageMetadata[] = (project as any).images ?? [];

    return from(images).pipe(
      map((image) => ({
        image,
        legacyFileUrl: (image as any).fileUrl,
      })),
      filter(({ legacyFileUrl }) => !!legacyFileUrl),

      mergeMap(({ image, legacyFileUrl }) => {
        const blob = this.base64ToBlob(legacyFileUrl);
        return this.projectImageService.createPreviewImage(blob).pipe(
          map((previewBlob) => ({
            blob,
            previewBlob,
            imageMetadata: ProjectImageMetadata.create(image.name, image.description ?? undefined, image.tags ?? undefined),
            projectId: project.id,
            syncState: SyncState.NEW,
          })),
        );
      }),

      toArray(),

      switchMap((allImageData) => {
        if (allImageData.length === 0) {
          return of(false);
        }
        // No project is passed on purpose: a migration must not bump lastModified, that would mark every
        // migrated project as out of sync and re-export it to the cloud.
        return this.projectImageService.saveImagesToIndexedDB(allImageData).pipe(
          tap(() => delete (project as any).images),
          map(() => true),
        );
      }),
    );
  }

  private base64ToBlob(base64: string): Blob {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }
}
